import React, { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  BoltIcon,
  StarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface UsageData {
  dailyMessagesUsed: number;
  dailyMessagesLimit: number;
  monthlyTokensUsed: number;
  monthlyTokensLimit: number;
  topUpBalance: number;
  plan: string;
}

const UsageDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get('/users/usage');
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usage) {
    return null;
  }

  const isFree = usage.plan === 'free';
  const messagePercent = usage.dailyMessagesLimit
    ? Math.min((usage.dailyMessagesUsed / usage.dailyMessagesLimit) * 100, 100)
    : 0;
  const tokenPercent = usage.monthlyTokensLimit
    ? Math.min((usage.monthlyTokensUsed / usage.monthlyTokensLimit) * 100, 100)
    : 0;

  const getBarColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-yellow-500';
    return 'bg-primary-500';
  };

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 px-4 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
        <ArrowTrendingUpIcon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
        <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
          {t('usage.title', 'Usage')}
        </span>
      </div>

      <div className="bg-light-bg dark:bg-dark-bg px-4 py-3 space-y-4">
        {/* Daily Messages (free tier) */}
        {isFree && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                <span className="text-sm text-light-text dark:text-dark-text">
                  {t('usage.dailyMessages', 'Daily Messages')}
                </span>
              </div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {usage.dailyMessagesUsed} / {usage.dailyMessagesLimit}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getBarColor(messagePercent)}`}
                style={{ width: `${messagePercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Monthly Tokens (paid tiers) */}
        {!isFree && usage.monthlyTokensLimit > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                <span className="text-sm text-light-text dark:text-dark-text">
                  {t('usage.monthlyTokens', 'Monthly Tokens')}
                </span>
              </div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {(usage.monthlyTokensUsed / 1000).toFixed(1)}K /{' '}
                {(usage.monthlyTokensLimit / 1000).toFixed(0)}K
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getBarColor(tokenPercent)}`}
                style={{ width: `${tokenPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Unlimited indicator */}
        {!isFree && usage.monthlyTokensLimit === 0 && (
          <div className="flex items-center gap-2">
            <BoltIcon className="w-4 h-4 text-primary-500" />
            <span className="text-sm text-light-text dark:text-dark-text">
              {t('usage.unlimitedTokens', 'Unlimited tokens')}
            </span>
          </div>
        )}

        {/* Top-up Balance */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StarIcon className="w-4 h-4 text-yellow-500" />
            <span className="text-sm text-light-text dark:text-dark-text">
              {t('usage.topUpBalance', 'Top-up Balance')}
            </span>
          </div>
          <span className="text-sm text-light-text dark:text-dark-text font-semibold">
            {usage.topUpBalance.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default UsageDashboard;
